import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import type { RootState } from '@src/app/store';
import type { Snapshot, SnapshotUiContext } from './types';
import { Recipe } from '@kemu-io/kemu-core/types';

/**
 * Arguments for creating a snapshot.
 */
export type CreateSnapshotArgs = {
  /** The id of the recipe in the in-memory pool */
  recipePoolId: string;
  /** Current Redux state to extract minimal UI context */
  state: RootState;
};

/**
 * Deep clones a cached recipe object while preserving the `storage` reference
 * to avoid duplicating potentially large binary buffers. Private properties (`$$`)
 * are kept intact.
 */
const cloneRecipeForSnapshot = (recipe: Recipe): Recipe => {
  // Preserve storage by removing it before stringify and re-attaching after
  // to avoid heavy deep copies of binary buffers.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { storage, globalVariables, ...storageLess } = recipe ?? {};
  const cloned = JSON.parse(JSON.stringify(storageLess));
  if (storage) {
    // Re-attach original storage reference
    cloned.storage = storage;
  }
  if (globalVariables) {
    cloned.globalVariables = globalVariables;
  }
  
  return cloned;
};

/**
 * Creates a snapshot of the current recipe and minimal UI context.
 * Returns a structure suitable for history capture and later reconciliation.
 */
export const createSnapshot = ({ recipePoolId, state }: CreateSnapshotArgs): Snapshot => {
  const recipe = findRecipe(recipePoolId);
  if (!recipe) {
    throw new Error(`Recipe [${recipePoolId}] not found`);
  }

  const recipeObject = cloneRecipeForSnapshot(recipe);
  const recipeThing = recipeObject.blocks[state.workspace.selectedBlockInfo?.recipeId as string];
  const canvasPosition = recipeThing?.canvas?.logicMapper.position;

  const uiContext: SnapshotUiContext = {
    selectedWidgetIds: (state.logicMapper.selectedWidgets || []).map(w => w.widgetId),
    openThingRecipeId: state.workspace.selectedBlockInfo?.recipeId,
    folderPath: state.workspace.folderPath?.map(p => p.groupId) ?? [],
    canvasZoom: state.logicMapper.canvasZoom,
    lastMouse: state.logicMapper.lastMouseClick,
    canvasPosition,
  };

  return { recipeObject, uiContext };
};

export default createSnapshot;


